import React, { ReactNode, createContext, useContext, useMemo, useState, useEffect } from "react";
import {
  BUSINESS_NEEDS_INFO_INIT,
  CONTACT_INFO_INIT,
  IBusinessNeed,
  ICompanyInfo,
  IContactData,
} from "../types/LeadRequisitionType";
import { IStep } from "../../types/commonTypes";
import { INIT_STEPS, errorCode } from "../constants/InitialConstants";
import { GET_LEAD_DATA_URL, GET_LEAD_ADMIN_SCORE, GET_LEAD_LIST } from "../api/apiUrls";
import { getRequest, getRequestData } from "../api/apiService";
import { contactUsOptions } from "../mockData";

type ContextType = {
  companyInfo: ICompanyInfo | null;
  setCompanyInfo: React.Dispatch<React.SetStateAction<ICompanyInfo | null>>;
  contactInfo: IContactData | null;
  setContactInfo: React.Dispatch<React.SetStateAction<IContactData>>;
  businessNeedInfo: IBusinessNeed | null;
  setBusinessNeedInfo: React.Dispatch<React.SetStateAction<IBusinessNeed>>;
  steps: IStep[];
  setSteps: React.Dispatch<React.SetStateAction<IStep[]>>;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  isBasicInfo: boolean;
  setIsBasicInfo: React.Dispatch<React.SetStateAction<boolean>>;
  isAboutBusiness: boolean;
  setIsAboutBusiness: React.Dispatch<React.SetStateAction<boolean>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  staticData: any;
  textAreaValue: any;
  setTextAreaValue: any;
  adminLeadData: any;
  leadList: any;
  isSubmitted: boolean;
  setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  urlFecth: string | null;
  setUrlFetch: React.Dispatch<React.SetStateAction<string | null>>;
  errorStatus: string | null;
  setErrorStatus: React.Dispatch<React.SetStateAction<string | null>>;
  isCardOpen: boolean;
  setIsCardOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCancelClicked: boolean;
  setIsCancelClicked: React.Dispatch<React.SetStateAction<boolean>>;
};

const DataContext = createContext<ContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [steps, setSteps] = useState<IStep[]>(INIT_STEPS);
  const [isBasicInfo, setIsBasicInfo] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isAboutBusiness, setIsAboutBusiness] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<ICompanyInfo | null>(null);
  const [staticData, setStaticData] = useState();
  const [textAreaValue, setTextAreaValue] = useState<any>([]);
  const [adminLeadData, setAdminLeadData] = useState();
  const [leadList, setLeadList] = useState();
  const [urlFecth, setUrlFetch] = useState<string | null>(null);
  const [isCardOpen, setIsCardOpen] = useState(false); // State to manage card visibility
  const [isCancelClicked, setIsCancelClicked] = useState(false); // State to check if Cancel button clicked.

  useEffect(() => {
    const contactUsPage = window.parent.location.href.includes("contact-us");
    setIsCardOpen(contactUsPage);
  }, []);

  const handleData = async (url: string) => {
    let response: any;
    let status, data;
    switch (url) {
      case GET_LEAD_DATA_URL:
        response = await getRequest(GET_LEAD_DATA_URL);
        ({ status, data } = response);
        setStaticData(status === 200 ? data[0] ? data[0]: data : contactUsOptions as unknown as typeof staticData);
        break;
      case GET_LEAD_ADMIN_SCORE:
        response = await getRequestData(GET_LEAD_ADMIN_SCORE);
        ({ status, data } = response);
        setAdminLeadData(data);
        handleResponse(status === 200 ? data : status);
        break;
      case GET_LEAD_LIST:
        response = await getRequestData(GET_LEAD_LIST);
        ({ status, data } = response);
        setLeadList(data);
        handleResponse(status === 200 ? data : status);
        break;
      default:
        console.log("URL not recognized");
    }
  };
  const handleResponse = (item: any) => {
    if (errorCode.includes(item)) {
      setErrorStatus("Something went wrong, please contact system administrator");
    }
  };
  useEffect(() => {
    if (urlFecth) {
      handleData(urlFecth);
    }
  }, [urlFecth]);
  const [contactInfo, setContactInfo] = useState<IContactData>(CONTACT_INFO_INIT);
  const [businessNeedInfo, setBusinessNeedInfo] = useState<IBusinessNeed>(BUSINESS_NEEDS_INFO_INIT);
  return useMemo(() => {
    return (
      <DataContext.Provider
        value={{
          steps,
          setTextAreaValue,
          textAreaValue,
          setSteps,
          companyInfo,
          urlFecth,
          setUrlFetch,
          setCompanyInfo,
          contactInfo,
          setContactInfo,
          businessNeedInfo,
          setBusinessNeedInfo,
          isBasicInfo,
          setIsBasicInfo,
          isEditMode,
          setIsEditMode,
          isAboutBusiness,
          setIsAboutBusiness,
          errorStatus,
          setErrorStatus,
          staticData,
          adminLeadData,
          leadList,
          isSubmitted,
          setIsSubmitted,
          error,
          setError,
          isCardOpen,
          setIsCardOpen,
          isCancelClicked,
          setIsCancelClicked,
        }}
      >
        {children}
      </DataContext.Provider>
    );
  }, [
    steps,
    companyInfo,
    contactInfo,
    businessNeedInfo,
    isEditMode,
    staticData,
    adminLeadData,
    leadList,
    textAreaValue,
    isSubmitted,
    isAboutBusiness,
    error,
    isBasicInfo,
    children,
    isCardOpen,
    isCancelClicked,
  ]);
};

export const useData = (): ContextType => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used with a DataProvider");
  }
  return context;
};
